import { create } from 'zustand';
import type { EntityStore } from './entityStore';
import type { ConnectionState, DeviceLive, Presence } from './types';

/**
 * Aggregate counters maintained incrementally from store changes (±1 per changed device),
 * never recomputed over all devices on each event (docs/architecture/06-frontend.md §5).
 */
interface Counters {
  online: number;
  offline: number;
  unknown: number;
  alarms: number;
}

const ZERO: Counters = { online: 0, offline: 0, unknown: 0, alarms: 0 };
export const usePresenceCounters = create<Counters>(() => ({ ...ZERO }));

/**
 * Counts the devices of `store` while subscribed. The counters start from zero and go back to zero when tracking stops
 * (sign-out): they describe this session's devices only, never the previous user's.
 */
export function trackPresence(store: EntityStore<DeviceLive>): () => void {
  const last = new Map<string, { p: Presence; a: boolean }>();
  usePresenceCounters.setState({ ...ZERO });
  for (const id of store.ids()) {
    const d = store.get(id);
    if (!d) continue;
    const now = { p: d.presence ?? 'unknown', a: Boolean(d.alarm) };
    last.set(id, now);
    usePresenceCounters.setState((s) => ({
      ...s,
      [now.p]: s[now.p] + 1,
      alarms: s.alarms + (now.a ? 1 : 0),
    }));
  }
  const unsubscribe = store.subscribeChanges((changed) => {
    const delta: Counters = { online: 0, offline: 0, unknown: 0, alarms: 0 };
    let touched = false;
    for (const id of changed) {
      const d = store.get(id);
      const prev = last.get(id);
      if (!d) {
        // Removed from the store: it no longer counts anywhere.
        if (prev) {
          delta[prev.p]--;
          if (prev.a) delta.alarms--;
          last.delete(id);
          touched = true;
        }
        continue;
      }
      const now = { p: d.presence ?? 'unknown', a: Boolean(d.alarm) };
      if (prev && prev.p === now.p && prev.a === now.a) continue;
      if (prev) {
        delta[prev.p]--;
        if (prev.a) delta.alarms--;
      }
      delta[now.p]++;
      if (now.a) delta.alarms++;
      last.set(id, now);
      touched = true;
    }
    if (touched)
      usePresenceCounters.setState((s) => ({
        online: s.online + delta.online,
        offline: s.offline + delta.offline,
        unknown: s.unknown + delta.unknown,
        alarms: s.alarms + delta.alarms,
      }));
  });
  return () => {
    unsubscribe();
    usePresenceCounters.setState({ ...ZERO });
  };
}

/** Realtime connection state for the status bar / banner (UI state, not device state). */
export const useConnection = create<{ state: ConnectionState }>(() => ({ state: 'offline' }));
